import {
  ChangeDetectorRef,
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { AuthorizationService } from '../services/authorization/authorization.service';

/**
 * Structural gate on a COMPONENT access claim.
 *
 * Usage: <app-financials *hasAccess="'FinancialsComponent'"></app-financials>
 *
 * Must stay structural (template-based), never an attribute directive that
 * removes the host element after the fact: an attribute directive's host
 * component is still instantiated before removal, so its init-time HTTP calls
 * fire and 403 for users without the backing API permission. Here the embedded
 * view is only created once the claim check passes, so a denied user's
 * component never exists at all.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[hasAccess]',
  standalone: true,
})
export class HasAccessDirective implements OnInit {
  private readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly authorizationService = inject(AuthorizationService);

  /** The COMPONENT claim key to check, e.g. *hasAccess="'TestComponent'". */
  @Input({ alias: 'hasAccess', required: true }) componentName!: string;

  async ngOnInit(): Promise<void> {
    const allowed = await this.authorizationService.hasAccessToComponent(
      this.componentName,
    );
    if (allowed) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      // Zoneless + OnPush: the claim check resolves after the init pass, so
      // the freshly created view needs a change-detection cycle scheduled.
      this.changeDetectorRef.markForCheck();
    }
  }
}
